/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

const num = (v: string | null, d: number) => {
  const n = v ? Number(v) : NaN
  return Number.isFinite(n) && n >= 0 ? n : d
}

export type TableState = {
  pageIndex: number
  pageSize: number
  search: string
}
export type TableSetters = {
  setPageIndex: (i: number) => void
  setPageSize: (s: number) => void
  setSearch: (q: string) => void
}

/**
 * Synchro état <-> URL (q, page, pageSize) + fallback localStorage par tableKey
 */
export function useTableUrlState(tableKey: string, defaults: TableState) {
  const [sp, setSp] = useSearchParams()

  // ---- lecture initiale: URL > localStorage > defaults
  const initial = useMemo<TableState>(() => {
    const fromUrl: TableState = {
      search: sp.get('q') ?? '',
      pageIndex: num(sp.get('page'), defaults.pageIndex),
      pageSize: num(sp.get('pageSize'), defaults.pageSize),
    }
    // si pas d'URL, tente localStorage
    if (![...sp.keys()].length) {
      try {
        const raw = localStorage.getItem(`tbl:${tableKey}`)
        if (raw) {
          const p = JSON.parse(raw)
          return {
            search: p.search ?? defaults.search,
            pageIndex: Number.isFinite(p.pageIndex)
              ? p.pageIndex
              : defaults.pageIndex,
            pageSize: Number.isFinite(p.pageSize)
              ? p.pageSize
              : defaults.pageSize,
          }
        }
      } catch {}
    }
    return fromUrl
  }, [sp, tableKey, defaults])

  // ---- setters qui poussent dans l’URL + localStorage
  const write = useCallback(
    (next: Partial<TableState>) => {
      const merged: TableState = {
        search: next.search ?? sp.get('q') ?? initial.search,
        pageIndex: next.pageIndex ?? num(sp.get('page'), initial.pageIndex),
        pageSize: next.pageSize ?? num(sp.get('pageSize'), initial.pageSize),
      }
      const nextSp = new URLSearchParams(sp)
      merged.search ? nextSp.set('q', merged.search) : nextSp.delete('q')
      nextSp.set('page', String(merged.pageIndex))
      nextSp.set('pageSize', String(merged.pageSize))
      setSp(nextSp, { replace: true }) // évite de spam l'historique
      try {
        localStorage.setItem(`tbl:${tableKey}`, JSON.stringify(merged))
      } catch {}
    },
    [sp, setSp, tableKey, initial],
  )

  const setPageIndex = useCallback(
    (i: number) => write({ pageIndex: i }),
    [write],
  )
  const setPageSize = useCallback(
    (s: number) => write({ pageSize: s, pageIndex: 0 }),
    [write],
  ) // reset page si pageSize change
  const setSearch = useCallback(
    (q: string) => write({ search: q, pageIndex: 0 }),
    [write],
  ) // reset page si filtre change

  // Expose l’état courant lu de l’URL (toujours source de vérité)
  const state: TableState = {
    search: sp.get('q') ?? initial.search,
    pageIndex: num(sp.get('page'), initial.pageIndex),
    pageSize: num(sp.get('pageSize'), initial.pageSize),
  }

  // Sauvegarde initiale dans localStorage (si URL déjà renseignée)
  useEffect(() => {
    try {
      localStorage.setItem(`tbl:${tableKey}`, JSON.stringify(state))
    } catch {}
  }, [tableKey, state])

  return { state, setPageIndex, setPageSize, setSearch } as const
}
